import React from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Container,
  Group,
  Image,
  Button,
  Text,
  Burger,
  Drawer,
  ScrollArea,
  Divider,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Menu,
  Avatar,
  UnstyledButton,
  rem,
} from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import classes from "./HeaderMegaMenu.module.css";
import { 
  IconSun, 
  IconMoon, 
  IconLogout, 
  IconChevronDown, 
  IconLayoutDashboard,
  IconSettings
} from "@tabler/icons-react";

// Context
import { useAuth } from "../context/AuthContext";

export function HeaderMegaMenu() {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { user, logout } = useAuth();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();

  // --- HELPER: Inisial Nama untuk Avatar ---
  const getInitials = (name) => {
    return name ? name.substring(0, 2).toUpperCase() : "US";
  };

  // --- DESKTOP: User Menu (Dropdown) ---
  const UserMenuDesktop = () => {
    // 1. Ambil Role String (Handle jika role berupa object atau string)
    let rawRole = "";
    const userRole = user?.role;

    if (typeof userRole === 'string') {
        rawRole = userRole;
    } else if (typeof userRole === 'object' && userRole !== null) {
        // Coba ambil dari properti umum (sesuaikan dengan respon backend Anda)
        rawRole = userRole.nama_role || userRole.name || userRole.slug || "";
    }

    // 2. Normalisasi ke huruf kecil agar mudah dicek
    const roleNormalized = rawRole.toLowerCase();

    // 3. Mapping Role ke Label Tampilan yang Bagus
    let displayRole = "User"; // Default

    switch (roleNormalized) {
        case 'admin':
        case 'administrator':
            displayRole = "Administrator";
            break;
        case 'supervisor':
            displayRole = "Supervisor";
            break;
        case 'sekolah':
            displayRole = "Pihak Sekolah";
            break;
        case 'penyedia':
        case 'mitra':
            displayRole = "Penyedia Makanan";
            break;
        default:
            // Jika role tidak dikenal, kapitalisasi huruf depannya saja
            // Contoh: "gudang" -> "Gudang"
            displayRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
    }

    return (
      <Menu
        width={260}
        position="bottom-end"
        transitionProps={{ transition: 'pop-top-right' }}
        withinPortal
      >
        <Menu.Target>
          <UnstyledButton className={classes.user}>
            <Group gap={7}>
              <Avatar src={null} alt={user?.name} radius="xl" size={30} color="blue">
                {getInitials(user?.name)}
              </Avatar>
              <div style={{ flex: 1 }}>
                  <Text fw={500} size="sm" lh={1} mr={3}>
                    {user?.name}
                  </Text>
                  
                  {/* --- LABEL ROLE YANG SUDAH DIPERBAIKI --- */}
                  <Text c="dimmed" size="xs" lh={1} mt={3}>
                    {displayRole} 
                  </Text>
                  {/* -------------------------------------- */}
                  
              </div>
              <IconChevronDown style={{ width: rem(12), height: rem(12) }} stroke={1.5} />
            </Group>
          </UnstyledButton>
        </Menu.Target>

        {/* ... (Isi Dropdown Menu biarkan tetap sama) ... */}
        <Menu.Dropdown>
            <Menu.Label>Pengaturan Akun</Menu.Label>
            
            <Menu.Item
                leftSection={<IconLayoutDashboard style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
                onClick={() => {
                    // Logika redirect sederhana berdasarkan role
                    if (roleNormalized === 'admin') navigate('/admin/dashboard');
                    else if (roleNormalized === 'supervisor') navigate('/supervisor/dashboard');
                    else if (roleNormalized === 'sekolah') navigate('/sekolah/dashboard'); // Pastikan route ini ada
                    else navigate('/'); 
                }}
            >
                Dashboard
            </Menu.Item>

            <Menu.Item
                leftSection={<IconSettings style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            >
                Pengaturan
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
                color="red"
                leftSection={<IconLogout style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
                onClick={logout}
            >
                Logout
            </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  };

  // --- COMPONENT: Tombol Dark Mode ---
  const DarkModeToggle = () => (
    <ActionIcon
      onClick={() => toggleColorScheme()}
      variant="default"
      size="lg"
      radius="md"
      aria-label="Toggle color scheme"
    >
      {colorScheme === "dark" ? (
        <IconSun size={18} stroke={1.5} />
      ) : (
        <IconMoon size={18} stroke={1.5} />
      )}
    </ActionIcon>
  );

  return (
    <Box className={classes.header}>
      <Container size="fluid" className={classes.inner} px="md">
        {/* --- KIRI: LOGO --- */}
        <Group gap={10} style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <Image src="/Logo-mbg-1.jpg" alt="Logo" h={35} w="auto" radius="sm" />
          <Text fw={800} size="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan', deg: 90 }}>
            GiziMon
          </Text>
        </Group>

        {/* --- KANAN: AUTH & TOOLS (DESKTOP) --- */}
        <Group visibleFrom="sm" gap="xs">
          <DarkModeToggle />
          
          {user ? (
            <UserMenuDesktop />
          ) : (
            <Button component={Link} to="/login" variant="filled" radius="md">
              Masuk Akun
            </Button>
          )}
        </Group>

        {/* --- MOBILE: BURGER ICON --- */}
        <Burger opened={drawerOpened} onClick={toggleDrawer} hiddenFrom="sm" size="sm" />
      </Container>

      {/* --- DRAWER (MOBILE MENU) --- */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="75%" // Ukuran drawer lebih nyaman
        padding="md"
        title={<Text fw={700}>Menu Navigasi</Text>}
        hiddenFrom="sm"
        zIndex={1000000}
      >
        <ScrollArea h={`calc(100vh - 80px)`} mx="-md">
          <Divider my="sm" />

          {/* User Profile di Mobile */}
          {user ? (
            <Box px="md" mb="md">
                <Group mb="md">
                    <Avatar size="lg" radius="xl" color="blue">{getInitials(user?.name)}</Avatar>
                    <div>
                        <Text fw={600}>{user.name}</Text>
                        <Text size="xs" c="dimmed">{user.email || user.role}</Text>
                    </div>
                </Group>
                
                <Button 
                    fullWidth 
                    variant="light" 
                    leftSection={<IconLayoutDashboard size={16}/>}
                    onClick={() => {
                        navigate(user?.role === 'admin' ? '/admin/dashboard' : '/supervisor/dashboard');
                        closeDrawer();
                    }}
                    mb="xs"
                >
                    Dashboard
                </Button>
                
                <Button 
                    fullWidth 
                    variant="outline" 
                    color="red"
                    leftSection={<IconLogout size={16}/>}
                    onClick={() => {
                        logout();
                        closeDrawer();
                    }}
                >
                    Logout
                </Button>
            </Box>
          ) : (
            <Box px="md" mb="md">
                <Button component={Link} to="/login" fullWidth size="md" radius="md">
                    Log in
                </Button>
            </Box>
          )}

          <Divider my="sm" />
          
          {/* Dark Mode di Mobile */}
          <Group justify="center" px="md">
             <Button
                fullWidth
                variant="default"
                onClick={() => toggleColorScheme()}
                leftSection={colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              >
                Ganti Tema ({colorScheme === "dark" ? "Light" : "Dark"})
              </Button>
          </Group>
        </ScrollArea>
      </Drawer>
    </Box>
  );
}